import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, fromNow } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/common";
import { Send, Hash, MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Chat() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const { data: conversations = [] } = useQuery({ queryKey: ["conversations"], queryFn: async () => { const d = (await api.get("/conversations")).data; return Array.isArray(d) ? d : []; }, refetchInterval: 8000 });
  const { data: messages = [] } = useQuery({
    queryKey: ["messages", active], enabled: !!active, refetchInterval: 4000,
    queryFn: async () => { const d = (await api.get(`/conversations/${active}/messages`)).data; return Array.isArray(d) ? d : []; },
  });

  useEffect(() => { if (!active && conversations.length) setActive(conversations[0].id); }, [conversations, active]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/conversations/${active}/messages`, { body: text });
    setText("");
    qc.invalidateQueries({ queryKey: ["messages", active] });
    qc.invalidateQueries({ queryKey: ["conversations"] });
  };

  const newChannel = async () => {
    const name = prompt("Kanal adı:");
    if (!name) return;
    await api.post("/conversations", { name, type: "channel", members: [] });
    qc.invalidateQueries({ queryKey: ["conversations"] });
    toast.success("Kanal oluşturuldu");
  };

  const activeConv = conversations.find((c) => c.id === active);

  return (
    <div>
      <div className="h-[calc(100vh-140px)] flex gap-4">
        <Card className="w-64 shrink-0 flex flex-col hidden md:flex">
          <div className="p-3 border-b flex items-center justify-between">
            <span className="font-semibold text-sm">Kanallar</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={newChannel} data-testid="new-channel-button"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {conversations.map((c) => (
              <button key={c.id} onClick={() => setActive(c.id)} className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-sm ${active === c.id ? "bg-indigo-600 text-white" : "hover:bg-muted"}`} data-testid={`channel-${c.id}`}>
                <Hash className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{c.name}</span>
                {c.unread > 0 && <Badge className="bg-rose-500 text-white text-[9px] h-4 min-w-4 px-1">{c.unread}</Badge>}
              </button>
            ))}
          </div>
        </Card>

        <Card className="flex-1 flex flex-col">
          {activeConv ? (
            <>
              <div className="p-3.5 border-b flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" /><span className="font-semibold">{activeConv.name}</span>
                <Badge variant="secondary" className="text-[10px] ml-auto">{activeConv.members?.length} üye</Badge>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((m) => {
                  const mine = m.user_id === user?.id;
                  return (
                    <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
                      <Avatar className="h-8 w-8 shrink-0"><AvatarFallback className="text-[10px]">{initials(m.user_name)}</AvatarFallback></Avatar>
                      <div className={`max-w-[70%] ${mine ? "items-end" : ""} flex flex-col`}>
                        <div className={`flex items-center gap-2 ${mine ? "flex-row-reverse" : ""}`}><span className="text-xs font-medium">{m.user_name}</span><span className="text-[10px] text-muted-foreground">{fromNow(m.created_at)}</span></div>
                        <div className={`mt-1 px-3 py-2 rounded-2xl text-sm ${mine ? "bg-indigo-600 text-white rounded-tr-sm" : "bg-muted rounded-tl-sm"}`}>{m.body}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <EmptyState icon={MessageSquare} title="Henüz mesaj yok" description="İlk mesajı gönderin." />}
              </div>
              <div className="p-3 border-t flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Mesaj yazın..." data-testid="chat-input" />
                <Button onClick={send} size="icon" data-testid="chat-send"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          ) : <EmptyState icon={MessageSquare} title="Kanal seçin" />}
        </Card>
      </div>
    </div>
  );
}
